import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAccount } from '@/api/useAccount';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { FIELD_CLASS } from '@/components/properties/AddressFields';
import { InvoiceCustomerLink } from '@/components/invoices/InvoiceCustomerLink';
import { toIsoDate, todayInZone } from '@/lib/timezone';
import { cn } from '@/lib/utils';
import {
  canVoid,
  hasLinkToCopy,
  InvoiceInvalidError,
  InvoiceRefusedError,
  useInvoiceActions,
  type Invoice,
} from '@/routes/invoices/useInvoices';

/**
 * The office's acts on an invoice.
 *
 * - **A draft** (M2-16, Kyle 2026-10-16): **Send invoice**, which emails the
 *   customer a link to their invoice page (`/send`), and **Mark as sent
 *   without emailing** (`/mark-sent`) for an invoice delivered by hand. Both
 *   take the same due date and freeze the same tax; only Send emails.
 * - **Anything sent and not void**: **Resend invoice** (`/resend`), and the
 *   customer's link — copy and revoke (`InvoiceCustomerLink`).
 * - **Anything not void**: Void — a draft too since M2-10 (Kyle, 2026-10-02).
 *
 * Send invoice and Resend ask first (Kyle 6a), naming the address the API
 * gives as `sendTo` — the send path's own lookup, so the dialog cannot name
 * an address the email would not go to. With no address there is nothing to
 * confirm: the request goes and the API's refusal sentence explains.
 *
 * Rendered only for `customers.manage`, and each act only in the status the
 * API accepts it in, so the screen never offers what the server is bound to
 * refuse. The server still decides: a refusal (409) comes back as its own
 * sentence — "Pat Morrow has no email address on file…" — and is shown as it
 * comes, never paraphrased. Every button is disabled while any act is in
 * flight, so two acts can never race from one screen.
 *
 * There is no Delete for a draft (M2-55); voiding one keeps its number on
 * record and, when its job is Invoiced, puts the job back to Complete so it
 * can be invoiced again (a Closed job stays Closed).
 */
export function InvoiceActs({
  invoice,
  onAnnounce,
}: {
  invoice: Invoice;
  onAnnounce: (message: string) => void;
}) {
  const { t } = useTranslation();
  const id = useId();
  const acts = useInvoiceActions(invoice.id);
  const [dueDate, setDueDate] = useState('');
  const [confirmingVoid, setConfirmingVoid] = useState(false);
  // Kyle 6a: Send and Resend ask first, naming the address the API says the
  // email would go to (`sendTo`, the send path's own lookup).
  const [confirmingEmail, setConfirmingEmail] = useState<'send' | 'resend' | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const number = Number(invoice.number);
  const timeZone = useAccount().data?.timezone;
  // A due date already past is allowed (Kyle, 2026-09-28) — backdating a job
  // billed late is real — but the invoice is Overdue the moment it is sent,
  // so the office is told first. "Past" is on the ACCOUNT's calendar, the one
  // the API derives Overdue on, not the viewer's.
  const pastDue =
    dueDate !== '' && timeZone !== undefined && dueDate < toIsoDate(todayInZone(timeZone));

  const pending =
    acts.send.isPending || acts.markSent.isPending || acts.resend.isPending || acts.void.isPending;

  function explain(error: unknown) {
    setProblem(
      error instanceof InvoiceRefusedError || error instanceof InvoiceInvalidError
        ? t('invoices.acts.refused', { reason: error.message })
        : t('invoices.acts.failed'),
    );
  }

  if (!canVoid(invoice.status)) return null;
  const draft = invoice.status === 'Draft';
  const due = dueDate === '' ? null : dueDate;
  const confirming = confirmingVoid || confirmingEmail !== null;

  function email(which: 'send' | 'resend') {
    setProblem(null);
    const done = {
      onSuccess: () => {
        setConfirmingEmail(null);
        onAnnounce(
          which === 'send'
            ? t('invoices.acts.sent', { number })
            : t('invoices.acts.resent', { number }),
        );
      },
      onError: explain,
    };
    if (which === 'send') acts.send.mutate(due, done);
    else acts.resend.mutate(undefined, done);
  }

  /**
   * Ask first when the API names an address. With none (`sendTo` null) there
   * is nothing to confirm: the request goes, and the API's refusal sentence —
   * "…has no email address on file…" — says what to do.
   */
  function ask(which: 'send' | 'resend') {
    if (!invoice.sendTo) {
      email(which);
      return;
    }
    setProblem(null);
    acts.send.reset();
    acts.resend.reset();
    setConfirmingEmail(which);
  }

  return (
    <section
      aria-labelledby={`${id}-heading`}
      className="space-y-4 rounded-lg border border-[var(--color-border)] p-4"
    >
      <h2 id={`${id}-heading`} className="text-base font-semibold">
        {t('invoices.acts.heading')}
      </h2>

      {draft ? (
        <div className="space-y-3">
          <div className="text-sm">
            <label htmlFor={`${id}-due`} className="mb-1 block font-medium">
              {t('invoices.acts.dueDate')}
            </label>
            <input
              id={`${id}-due`}
              type="date"
              className={FIELD_CLASS}
              value={dueDate}
              aria-describedby={cn(`${id}-due-hint`, pastDue && `${id}-due-past`)}
              onChange={(event) => setDueDate(event.target.value)}
            />
            <p id={`${id}-due-hint`} className="mt-1 text-xs text-[var(--color-muted-foreground)]">
              {t('invoices.acts.dueDateHint')}
            </p>
          </div>
          {/* Always rendered, so the warning is announced when it appears. */}
          <div aria-live="polite">
            {pastDue ? (
              <p
                id={`${id}-due-past`}
                className="text-sm font-medium text-[var(--color-destructive)]"
                data-testid="due-date-past"
              >
                {t('invoices.acts.dueDatePast')}
              </p>
            ) : null}
          </div>
          <div className="flex flex-wrap gap-3">
            <div className="space-y-1">
              <Button
                type="button"
                disabled={pending}
                aria-describedby={`${id}-send-help`}
                onClick={() => ask('send')}
              >
                {acts.send.isPending ? t('invoices.acts.sending') : t('invoices.acts.send')}
              </Button>
              <p id={`${id}-send-help`} className="text-xs text-[var(--color-muted-foreground)]">
                {t('invoices.acts.sendHelp')}
              </p>
            </div>
            <div className="space-y-1">
              <Button
                type="button"
                variant="outline"
                disabled={pending}
                aria-describedby={`${id}-mark-help`}
                onClick={() => {
                  setProblem(null);
                  acts.markSent.mutate(due, {
                    onSuccess: () => onAnnounce(t('invoices.acts.markedSent', { number })),
                    onError: explain,
                  });
                }}
              >
                {acts.markSent.isPending
                  ? t('invoices.acts.markingSent')
                  : t('invoices.acts.markSent')}
              </Button>
              <p id={`${id}-mark-help`} className="text-xs text-[var(--color-muted-foreground)]">
                {t('invoices.acts.markSentHelp')}
              </p>
            </div>
          </div>
        </div>
      ) : null}

      {hasLinkToCopy(invoice.status) ? (
        <div className="space-y-1">
          <Button
            type="button"
            disabled={pending}
            aria-describedby={`${id}-resend-help`}
            onClick={() => ask('resend')}
          >
            {acts.resend.isPending ? t('invoices.acts.resending') : t('invoices.acts.resend')}
          </Button>
          <p id={`${id}-resend-help`} className="text-xs text-[var(--color-muted-foreground)]">
            {t('invoices.acts.resendHelp')}
          </p>
        </div>
      ) : null}

      {problem && !confirming ? (
        <p role="alert" className="text-sm text-[var(--color-destructive)]">
          {problem}
        </p>
      ) : null}

      {hasLinkToCopy(invoice.status) ? <InvoiceCustomerLink invoice={invoice} /> : null}

      <div className="space-y-2">
        <p className="text-sm text-[var(--color-muted-foreground)]" data-testid="void-help">
          {draft ? t('invoices.acts.voidHelpDraft') : t('invoices.acts.voidHelp')}
        </p>
        <Button
          type="button"
          variant="outline"
          disabled={pending}
          onClick={() => {
            // The mutation outlives the dialog, so a failure from an earlier
            // attempt must not greet this one (web/CONVENTIONS.md, M1-27).
            acts.void.reset();
            setConfirmingVoid(true);
          }}
        >
          {t('invoices.acts.void')}
        </Button>
      </div>

      {confirmingEmail !== null && invoice.sendTo ? (
        <ConfirmDialog
          titleId={`${id}-email-title`}
          testId="email-confirm"
          title={
            confirmingEmail === 'send'
              ? t('invoices.acts.sendConfirmTitle', { number, address: invoice.sendTo })
              : t('invoices.acts.resendConfirmTitle', { number, address: invoice.sendTo })
          }
          description={
            confirmingEmail === 'send'
              ? t('invoices.acts.sendConfirmDescription')
              : t('invoices.acts.resendConfirmDescription')
          }
          confirmLabel={confirmingEmail === 'send' ? t('invoices.acts.send') : t('invoices.acts.resend')}
          destructive={false}
          isPending={confirmingEmail === 'send' ? acts.send.isPending : acts.resend.isPending}
          errorMessage={problem ?? undefined}
          onConfirm={() => email(confirmingEmail)}
          onClose={() => setConfirmingEmail(null)}
        />
      ) : null}

      {confirmingVoid ? (
        <ConfirmDialog
          titleId={`${id}-void-title`}
          title={t('invoices.acts.voidTitle', { number })}
          description={
            draft ? t('invoices.acts.voidDescriptionDraft') : t('invoices.acts.voidDescription')
          }
          confirmLabel={t('invoices.acts.voidConfirm')}
          destructive
          isPending={acts.void.isPending}
          errorMessage={acts.void.isError ? (problem ?? undefined) : undefined}
          onConfirm={() => {
            setProblem(null);
            acts.void.mutate(undefined, {
              onSuccess: () => {
                setConfirmingVoid(false);
                onAnnounce(t('invoices.acts.voided', { number }));
              },
              onError: explain,
            });
          }}
          onClose={() => setConfirmingVoid(false)}
        />
      ) : null}
    </section>
  );
}
